import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Lets the active tab of a page announce itself to the header breadcrumb, so the
 * header reads "Finance › Customer status" without the header having to know
 * every page's tab list. {@link TabBar} registers automatically.
 */
interface BreadcrumbContextType {
  tab: string | null;
  setTab: (tab: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType>({ tab: null, setTab: () => {} });

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [tab, setTab] = useState<string | null>(null);
  return <BreadcrumbContext.Provider value={{ tab, setTab }}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbTab() {
  return useContext(BreadcrumbContext).tab;
}

/** Registers `label` as the breadcrumb's active tab while the caller is mounted. */
export function useRegisterBreadcrumbTab(label: string | null | undefined) {
  const { setTab } = useContext(BreadcrumbContext);
  useEffect(() => {
    if (!label) return;
    setTab(label);
    return () => setTab(null);
  }, [label, setTab]);
}
